import { Box } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SearchFilters, FilterChipDef } from "./filterChipDefs";
import { ThemeMode } from "../../theme/ThemeContext";
import { useTypeaheadSearch, useInteractionTags } from "../../../hooks/useInteraction";
import { useSites } from "../../../hooks/useSite";

// ---------------------------------------------------------------------------
// Shared popover colors (portal-rendered, outside V2 theme div)
// ---------------------------------------------------------------------------

function popoverColors(mode: ThemeMode) {
  return mode === "dark"
    ? {
        bg: "#1A2332",
        border: "rgba(148,163,184,0.15)",
        shadow: "0 8px 24px rgba(0,0,0,0.5)",
        text: "#F1F5F9",
        textSecondary: "#94A3B8",
        inputBg: "#111827",
        inputBorder: "rgba(148,163,184,0.15)",
        accent: "#06D6A0",
        accentDim: "rgba(6,214,160,0.15)",
        hover: "#1E293B",
        muted: "#475569",
      }
    : {
        bg: "#FFFFFF",
        border: "rgba(15,23,42,0.12)",
        shadow: "0 8px 24px rgba(0,0,0,0.12)",
        text: "#0F172A",
        textSecondary: "#475569",
        inputBg: "#F1F5F9",
        inputBorder: "rgba(15,23,42,0.12)",
        accent: "#059669",
        accentDim: "rgba(5,150,105,0.1)",
        hover: "#E2E8F0",
        muted: "#94A3B8",
      };
}

// ---------------------------------------------------------------------------
// Shared input style
// ---------------------------------------------------------------------------

function inputSx(c: ReturnType<typeof popoverColors>) {
  return {
    width: "100%",
    px: "10px",
    py: "7px",
    fontSize: "0.8125rem",
    fontFamily: "inherit",
    color: c.text,
    backgroundColor: c.inputBg,
    border: `1px solid ${c.inputBorder}`,
    borderRadius: "6px",
    outline: "none",
    boxSizing: "border-box" as const,
    "&:focus": { borderColor: c.accent },
    "&::placeholder": { color: c.muted },
  };
}

function btnSx(c: ReturnType<typeof popoverColors>, primary = false) {
  return {
    px: "12px",
    py: "5px",
    fontSize: "0.75rem",
    fontWeight: 600,
    fontFamily: "inherit",
    borderRadius: "6px",
    cursor: "pointer",
    border: primary ? "none" : `1px solid ${c.inputBorder}`,
    backgroundColor: primary ? c.accent : "transparent",
    color: primary ? "#fff" : c.text,
    "&:hover": {
      backgroundColor: primary ? c.accent : c.hover,
      opacity: primary ? 0.9 : 1,
    },
  };
}

// ---------------------------------------------------------------------------
// Popover content props
// ---------------------------------------------------------------------------

export interface FilterPopoverContentProps {
  chipDef: FilterChipDef;
  filters: SearchFilters;
  onFiltersChange: (f: SearchFilters) => void;
  onClose: () => void;
  mode: ThemeMode;
  customerId?: string;
}

type PopoverBaseProps = Omit<FilterPopoverContentProps, "chipDef">;
type PopoverWithChipDef = PopoverBaseProps & { chipDef: FilterChipDef };

// ---------------------------------------------------------------------------
// Date popover
// ---------------------------------------------------------------------------

function DatePopover({ filters, onFiltersChange, onClose, mode }: PopoverBaseProps) {
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);
  const toDatetimeLocal = (iso: string | undefined) => {
    if (!iso) return "";
    // Accept either "YYYY-MM-DD" or a full ISO string; truncate to "YYYY-MM-DDTHH:mm"
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(0, 16);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const [from, setFrom] = useState(toDatetimeLocal(filters.from));
  const [to, setTo] = useState(toDatetimeLocal(filters.to));

  const handleApply = () => {
    onFiltersChange({
      ...filters,
      from: from ? `${from}:00.000Z` : undefined,
      to: to ? `${to}:59.999Z` : undefined,
    });
    onClose();
  };

  const handleClear = () => {
    onFiltersChange({ ...filters, from: undefined, to: undefined });
    onClose();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", p: "14px", minWidth: 260 }}>
      <Box component="label" sx={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: c.textSecondary }}>
        {t("common:from")}
      </Box>
      <Box
        component="input"
        type="datetime-local"
        value={from}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFrom(e.target.value)}
        sx={{
          ...inputSx(c),
          colorScheme: mode === "dark" ? "dark" : "light",
          "&::-webkit-calendar-picker-indicator": { filter: mode === "dark" ? "invert(1)" : "none" },
        }}
      />
      <Box component="label" sx={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: c.textSecondary }}>
        {t("common:to")}
      </Box>
      <Box
        component="input"
        type="datetime-local"
        value={to}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTo(e.target.value)}
        sx={{
          ...inputSx(c),
          colorScheme: mode === "dark" ? "dark" : "light",
          "&::-webkit-calendar-picker-indicator": { filter: mode === "dark" ? "invert(1)" : "none" },
        }}
      />
      <Box sx={{ display: "flex", gap: "8px", justifyContent: "flex-end", mt: "4px" }}>
        <Box component="button" onClick={handleClear} sx={btnSx(c)}>{t("common:clear")}</Box>
        <Box component="button" onClick={handleApply} sx={btnSx(c, true)}>{t("common:apply")}</Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Agent typeahead popover
// ---------------------------------------------------------------------------

function AgentPopover({ filters, onFiltersChange, onClose, mode, customerId }: PopoverBaseProps) {
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);
  const [input, setInput] = useState(filters.agentName || "");
  const [exactMatch, setExactMatch] = useState(filters.agentNameExactMatch || false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { data: suggestions } = useTypeaheadSearch("agentName", "AgentName", input, customerId);

  const handleSelect = (value: string) => {
    setInput(value);
    setShowSuggestions(false);
  };

  const handleApply = () => {
    onFiltersChange({
      ...filters,
      agentName: input || undefined,
      agentNameExactMatch: input ? exactMatch : undefined,
    });
    onClose();
  };

  const handleClear = () => {
    onFiltersChange({ ...filters, agentName: undefined, agentNameExactMatch: undefined });
    onClose();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", p: "14px", minWidth: 260, position: "relative" }}>
      <Box component="label" sx={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: c.textSecondary }}>
        {t("search:agentName")}
      </Box>
      <Box
        component="input"
        type="text"
        value={input}
        placeholder={t("search:typeToSearch")}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setInput(e.target.value); setShowSuggestions(true); }}
        onFocus={() => setShowSuggestions(true)}
        sx={inputSx(c)}
      />
      {showSuggestions && suggestions && suggestions.length > 0 && (
        <Box sx={{ maxHeight: 160, overflowY: "auto", borderRadius: "6px", border: `1px solid ${c.inputBorder}`, bgcolor: c.inputBg }}>
          {suggestions.map((s) => (
            <Box
              key={s.value}
              component="button"
              onClick={() => handleSelect(s.friendlyValue || s.value)}
              sx={{
                display: "block",
                width: "100%",
                textAlign: "left",
                px: "10px",
                py: "6px",
                fontSize: "0.8125rem",
                color: c.text,
                bgcolor: "transparent",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                "&:hover": { bgcolor: c.hover },
              }}
            >
              {s.friendlyValue || s.value}
            </Box>
          ))}
        </Box>
      )}
      <Box
        component="label"
        sx={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.75rem", color: c.textSecondary, cursor: "pointer", userSelect: "none" }}
      >
        <Box
          component="input"
          type="checkbox"
          checked={exactMatch}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExactMatch(e.target.checked)}
          sx={{ accentColor: c.accent }}
        />
        {t("search:exactMatch")}
      </Box>
      <Box sx={{ display: "flex", gap: "8px", justifyContent: "flex-end", mt: "4px" }}>
        <Box component="button" onClick={handleClear} sx={btnSx(c)}>{t("common:clear")}</Box>
        <Box component="button" onClick={handleApply} sx={btnSx(c, true)}>{t("common:apply")}</Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Phone typeahead popover
// ---------------------------------------------------------------------------

function PhonePopover({ filters, onFiltersChange, onClose, mode, customerId }: PopoverBaseProps) {
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);
  const [input, setInput] = useState(filters.phoneNumber || "");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { data: suggestions } = useTypeaheadSearch("phoneNumber", "PhoneNumber", input, customerId);

  const handleSelect = (value: string) => {
    setInput(value);
    setShowSuggestions(false);
  };

  const handleApply = () => {
    onFiltersChange({ ...filters, phoneNumber: input || undefined });
    onClose();
  };

  const handleClear = () => {
    onFiltersChange({ ...filters, phoneNumber: undefined });
    onClose();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", p: "14px", minWidth: 260 }}>
      <Box component="label" sx={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: c.textSecondary }}>
        {t("search:phoneNumber")}
      </Box>
      <Box
        component="input"
        type="text"
        value={input}
        placeholder={t("search:typeToSearch")}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setInput(e.target.value); setShowSuggestions(true); }}
        onFocus={() => setShowSuggestions(true)}
        sx={inputSx(c)}
      />
      {showSuggestions && suggestions && suggestions.length > 0 && (
        <Box sx={{ maxHeight: 160, overflowY: "auto", borderRadius: "6px", border: `1px solid ${c.inputBorder}`, bgcolor: c.inputBg }}>
          {suggestions.map((s) => (
            <Box
              key={s.value}
              component="button"
              onClick={() => handleSelect(s.friendlyValue || s.value)}
              sx={{
                display: "block",
                width: "100%",
                textAlign: "left",
                px: "10px",
                py: "6px",
                fontSize: "0.8125rem",
                color: c.text,
                bgcolor: "transparent",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                "&:hover": { bgcolor: c.hover },
              }}
            >
              {s.friendlyValue || s.value}
            </Box>
          ))}
        </Box>
      )}
      <Box sx={{ display: "flex", gap: "8px", justifyContent: "flex-end", mt: "4px" }}>
        <Box component="button" onClick={handleClear} sx={btnSx(c)}>{t("common:clear")}</Box>
        <Box component="button" onClick={handleApply} sx={btnSx(c, true)}>{t("common:apply")}</Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Select popover (Type, Direction)
// ---------------------------------------------------------------------------

interface SelectOption {
  value: string;
  label: string;
}

const TYPE_OPTIONS: SelectOption[] = [
  { value: "Audio", label: "audio" },
  { value: "Video", label: "video" },
  { value: "AudioAndVideo", label: "audioAndVideo" },
  { value: "Chat", label: "chat" },
  { value: "Email", label: "email" },
];

const DIRECTION_OPTIONS: SelectOption[] = [
  { value: "in", label: "inbound" },
  { value: "out", label: "outbound" },
];

function SelectPopover({ chipDef, filters, onFiltersChange, onClose, mode }: PopoverWithChipDef) {
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);
  const options = chipDef.id === "direction" ? DIRECTION_OPTIONS : TYPE_OPTIONS;
  const filterKey = chipDef.id === "direction" ? "direction" : "interactionType";
  const current = filters[filterKey as keyof SearchFilters] as string | undefined;

  const handleSelect = (value: string) => {
    onFiltersChange({ ...filters, [filterKey]: value });
    onClose();
  };

  const handleClear = () => {
    onFiltersChange({ ...filters, [filterKey]: undefined });
    onClose();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", p: "8px", minWidth: 180 }}>
      {options.map((opt) => (
        <Box
          key={opt.value}
          component="button"
          onClick={() => handleSelect(opt.value)}
          sx={{
            display: "block",
            width: "100%",
            textAlign: "left",
            px: "10px",
            py: "7px",
            fontSize: "0.8125rem",
            fontFamily: "inherit",
            color: current === opt.value ? c.accent : c.text,
            bgcolor: current === opt.value ? c.accentDim : "transparent",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontWeight: current === opt.value ? 600 : 400,
            "&:hover": { bgcolor: c.hover },
          }}
        >
          {t(`search:${opt.label}`)}
        </Box>
      ))}
      <Box sx={{ borderTop: `1px solid ${c.inputBorder}`, mt: "4px", pt: "4px" }}>
        <Box
          component="button"
          onClick={handleClear}
          sx={{
            display: "block",
            width: "100%",
            textAlign: "left",
            px: "10px",
            py: "6px",
            fontSize: "0.75rem",
            fontFamily: "inherit",
            color: c.muted,
            bgcolor: "transparent",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            "&:hover": { bgcolor: c.hover },
          }}
        >
          {t("common:clear")}
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Site popover
// ---------------------------------------------------------------------------

function SitePopover({ filters, onFiltersChange, onClose, mode, customerId }: PopoverBaseProps) {
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);
  const { data: sitesData } = useSites(customerId);
  const sites = sitesData?.sites || [];
  const [search, setSearch] = useState("");

  const filtered = search
    ? sites.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()))
    : sites;

  const handleSelect = (siteId: string) => {
    onFiltersChange({ ...filters, siteId });
    onClose();
  };

  const handleClear = () => {
    onFiltersChange({ ...filters, siteId: undefined });
    onClose();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", p: "14px", minWidth: 240 }}>
      <Box component="input" type="text" value={search} placeholder={t("search:searchSites")} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} sx={inputSx(c)} />
      <Box sx={{ maxHeight: 200, overflowY: "auto", display: "flex", flexDirection: "column", gap: "2px" }}>
        {filtered.map((s) => (
          <Box
            key={s.siteId}
            component="button"
            onClick={() => handleSelect(s.siteId)}
            sx={{
              display: "block",
              width: "100%",
              textAlign: "left",
              px: "10px",
              py: "6px",
              fontSize: "0.8125rem",
              fontFamily: "inherit",
              color: filters.siteId === s.siteId ? c.accent : c.text,
              bgcolor: filters.siteId === s.siteId ? c.accentDim : "transparent",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: filters.siteId === s.siteId ? 600 : 400,
              "&:hover": { bgcolor: c.hover },
            }}
          >
            {s.name}
          </Box>
        ))}
        {filtered.length === 0 && (
          <Box sx={{ px: "10px", py: "6px", fontSize: "0.75rem", color: c.muted }}>{t("common:noSitesFound")}</Box>
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "2px" }}>
        <Box component="button" onClick={handleClear} sx={btnSx(c)}>{t("common:clear")}</Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Tags multi-select popover
// ---------------------------------------------------------------------------

function TagsPopover({ filters, onFiltersChange, onClose, mode, customerId }: PopoverBaseProps) {
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);
  const { data: allTags } = useInteractionTags(customerId);
  const [selected, setSelected] = useState<Set<string>>(new Set(filters.tags || []));
  const [search, setSearch] = useState("");

  const tags = allTags || [];
  const filtered = search ? tags.filter((t) => t.toLowerCase().includes(search.toLowerCase())) : tags;

  const toggle = (tag: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  const handleApply = () => {
    const arr = Array.from(selected);
    onFiltersChange({ ...filters, tags: arr.length > 0 ? arr : undefined });
    onClose();
  };

  const handleClear = () => {
    onFiltersChange({ ...filters, tags: undefined });
    onClose();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", p: "14px", minWidth: 240 }}>
      <Box component="input" type="text" value={search} placeholder={t("search:searchTags")} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} sx={inputSx(c)} />
      <Box sx={{ maxHeight: 200, overflowY: "auto", display: "flex", flexDirection: "column", gap: "2px" }}>
        {filtered.map((tag) => (
          <Box
            key={tag}
            component="label"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              px: "10px",
              py: "5px",
              fontSize: "0.8125rem",
              color: c.text,
              cursor: "pointer",
              borderRadius: "4px",
              "&:hover": { bgcolor: c.hover },
            }}
          >
            <Box
              component="input"
              type="checkbox"
              checked={selected.has(tag)}
              onChange={() => toggle(tag)}
              sx={{ accentColor: c.accent }}
            />
            {tag}
          </Box>
        ))}
        {filtered.length === 0 && (
          <Box sx={{ px: "10px", py: "6px", fontSize: "0.75rem", color: c.textSecondary }}>{t("common:noTagsFound")}</Box>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: "8px", justifyContent: "flex-end", mt: "4px" }}>
        <Box component="button" onClick={handleClear} sx={btnSx(c)}>{t("common:clear")}</Box>
        <Box component="button" onClick={handleApply} sx={btnSx(c, true)}>{t("common:apply")}</Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Text input popover (generic)
// ---------------------------------------------------------------------------

function TextPopover({ chipDef, filters, onFiltersChange, onClose, mode }: PopoverWithChipDef) {
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);
  const filterKey = chipDef.id as keyof SearchFilters;
  const [input, setInput] = useState((filters[filterKey] as string) || "");

  const handleApply = () => {
    onFiltersChange({ ...filters, [filterKey]: input || undefined });
    onClose();
  };

  const handleClear = () => {
    onFiltersChange({ ...filters, [filterKey]: undefined });
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleApply();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", p: "14px", minWidth: 240 }}>
      <Box component="label" sx={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: c.textSecondary }}>
        {chipDef.label}
      </Box>
      <Box component="input" type="text" value={input} placeholder={`Enter ${chipDef.label.toLowerCase()}...`} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(e.target.value)} onKeyDown={handleKeyDown} sx={inputSx(c)} />
      <Box sx={{ display: "flex", gap: "8px", justifyContent: "flex-end", mt: "4px" }}>
        <Box component="button" onClick={handleClear} sx={btnSx(c)}>{t("common:clear")}</Box>
        <Box component="button" onClick={handleApply} sx={btnSx(c, true)}>{t("common:apply")}</Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Boolean popover (three-state: Yes / No / Any)
// ---------------------------------------------------------------------------

function BooleanPopover({ chipDef, filters, onFiltersChange, onClose, mode }: PopoverWithChipDef) {
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);
  const filterKey = chipDef.id as keyof SearchFilters;
  const current = filters[filterKey] as boolean | undefined;

  const handleSelect = (value: boolean | undefined) => {
    onFiltersChange({ ...filters, [filterKey]: value });
    onClose();
  };

  const options: { value: boolean | undefined; labelKey: string }[] = [
    { value: true, labelKey: "yes" },
    { value: false, labelKey: "no" },
    { value: undefined, labelKey: "any" },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", p: "8px", minWidth: 140 }}>
      {options.map((opt) => {
        const isActive = current === opt.value;
        return (
          <Box
            key={opt.labelKey}
            component="button"
            onClick={() => handleSelect(opt.value)}
            sx={{
              display: "block",
              width: "100%",
              textAlign: "left",
              px: "10px",
              py: "7px",
              fontSize: "0.8125rem",
              fontFamily: "inherit",
              color: isActive ? c.accent : c.text,
              bgcolor: isActive ? c.accentDim : "transparent",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: isActive ? 600 : 400,
              "&:hover": { bgcolor: c.hover },
            }}
          >
            {t(`common:${opt.labelKey}`)}
          </Box>
        );
      })}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Duration popover
// ---------------------------------------------------------------------------

function DurationPopover({ filters, onFiltersChange, onClose, mode }: PopoverBaseProps) {
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);
  const [min, setMin] = useState(filters.minDuration != null ? String(filters.minDuration) : "");
  const [max, setMax] = useState(filters.maxDuration != null ? String(filters.maxDuration) : "");

  const handleApply = () => {
    onFiltersChange({
      ...filters,
      minDuration: min ? Number(min) : undefined,
      maxDuration: max ? Number(max) : undefined,
    });
    onClose();
  };

  const handleClear = () => {
    onFiltersChange({ ...filters, minDuration: undefined, maxDuration: undefined });
    onClose();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", p: "14px", minWidth: 240 }}>
      <Box component="label" sx={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: c.textSecondary }}>
        {t("search:minSeconds")}
      </Box>
      <Box component="input" type="number" min={0} value={min} placeholder="0" onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMin(e.target.value)} sx={inputSx(c)} />
      <Box component="label" sx={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: c.textSecondary }}>
        {t("search:maxSeconds")}
      </Box>
      <Box component="input" type="number" min={0} value={max} placeholder={t("search:noLimit")} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMax(e.target.value)} sx={inputSx(c)} />
      <Box sx={{ display: "flex", gap: "8px", justifyContent: "flex-end", mt: "4px" }}>
        <Box component="button" onClick={handleClear} sx={btnSx(c)}>{t("common:clear")}</Box>
        <Box component="button" onClick={handleApply} sx={btnSx(c, true)}>{t("common:apply")}</Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Dispatcher — returns the right popover content for a chip
// ---------------------------------------------------------------------------

export default function FilterPopoverContent(props: FilterPopoverContentProps) {
  const { chipDef, ...rest } = props;

  // Special-case by chip id for typeahead variants
  if (chipDef.id === "agent") return <AgentPopover {...rest} />;
  if (chipDef.id === "phoneNumber") return <PhonePopover {...rest} />;

  switch (chipDef.popoverType) {
    case "date":
      return <DatePopover {...rest} />;
    case "select":
      return <SelectPopover chipDef={chipDef} {...rest} />;
    case "site":
      return <SitePopover {...rest} />;
    case "tags":
      return <TagsPopover {...rest} />;
    case "text":
      return <TextPopover chipDef={chipDef} {...rest} />;
    case "boolean":
      return <BooleanPopover chipDef={chipDef} {...rest} />;
    case "duration":
      return <DurationPopover {...rest} />;
    default:
      return <TextPopover chipDef={chipDef} {...rest} />;
  }
}
